// Lightweight YAML frontmatter splitter. Matches the "--- ... ---" block at
// the very start of a text document and returns the parsed map alongside
// the remaining body. We don't pull in a YAML dep here — server-side
// parsing (the metadata-enrichment pipeline) already stores structured
// frontmatter on the sidecar. This client-side splitter only needs to
// remove the raw fence so the markdown renderer doesn't display it
// verbatim AND to optionally surface a quick preview when the sidecar
// hasn't loaded yet.

export interface FrontmatterSplit {
  /** Raw frontmatter text (between the fences), or null if not present. */
  rawFrontmatter: string | null;
  /** Parsed key→value map. Values are strings (multi-line preserved) or
   *  the original raw line when parsing is ambiguous. Empty if absent. */
  frontmatter: Record<string, string>;
  /** The document body with the frontmatter stripped. */
  body: string;
}

const FENCE_RE = /^---\s*\n([\s\S]*?)\n---\s*\n?/;

/**
 * Split a string into frontmatter + body. Recognizes `---\n...\n---\n` at
 * position 0 only (per CommonMark frontmatter convention).
 */
export function splitFrontmatter(text: string): FrontmatterSplit {
  if (!text) return { rawFrontmatter: null, frontmatter: {}, body: text ?? '' };
  const m = FENCE_RE.exec(text);
  if (!m) return { rawFrontmatter: null, frontmatter: {}, body: text };
  const rawFrontmatter = m[1] ?? '';
  const body = text.slice(m[0].length);
  return {
    rawFrontmatter,
    frontmatter: parseSimpleYaml(rawFrontmatter),
    body,
  };
}

/**
 * Tiny YAML-ish parser — handles flat `key: value` pairs and trivial
 * "key:" + indented multi-line values. Lists and nested mappings render
 * as the raw text. We deliberately don't ship a full YAML parser here;
 * the structured copy of frontmatter lives on the sidecar API.
 */
function parseSimpleYaml(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const lines = raw.split('\n');
  let currentKey: string | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentKey !== null) {
      out[currentKey] = buffer.join('\n').replace(/\s+$/g, '');
    }
  };

  for (const line of lines) {
    // New key at column 0?
    const kvMatch = /^([A-Za-z_][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (kvMatch && !line.startsWith(' ') && !line.startsWith('\t')) {
      flush();
      currentKey = kvMatch[1]!;
      const rest = kvMatch[2] ?? '';
      buffer = rest ? [rest] : [];
    } else if (currentKey !== null) {
      // Continuation line — indented value content
      buffer.push(line.replace(/^\s{1,4}/, ''));
    }
  }
  flush();
  return out;
}
